import { Button } from "@mui/material";
import { type ReactNode, useCallback, useMemo, useState } from "react";

import { BaseDialog } from "@mr-portfolio-components/atoms/base-dialog";
import { useLabel } from "@mr-portfolio-infrastructure/translations/use-label";

import { type ConfirmDialogApi, ConfirmDialogContext, type ConfirmDialogOptions } from "./confirm-dialog-context";

interface PendingConfirmation {
  options: ConfirmDialogOptions;
  resolve: (isConfirmed: boolean) => void;
}

export function ConfirmDialogProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const getLabel = useLabel();
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);

  const confirm = useCallback(
    (options: ConfirmDialogOptions) =>
      new Promise<boolean>((resolve) => {
        setPendingConfirmation({ options, resolve });
      }),
    [],
  );

  const confirmDialogApi = useMemo<ConfirmDialogApi>(
    () => ({
      confirm,
      confirmDelete: (options) =>
        confirm({
          title: getLabel("dialogs.deleteConfirm.title"),
          content: getLabel("dialogs.deleteConfirm.content"),
          confirmButtonTitle: getLabel("app.buttons.delete"),
          isDestructive: true,
          ...options,
        }),
    }),
    [confirm, getLabel],
  );

  const closeDialog = (isConfirmed: boolean): void => {
    pendingConfirmation?.resolve(isConfirmed);
    setPendingConfirmation(null);
  };

  const options = pendingConfirmation?.options;

  return (
    <ConfirmDialogContext.Provider value={confirmDialogApi}>
      {children}
      <BaseDialog
        open={pendingConfirmation !== null}
        onClose={() => closeDialog(false)}
        title={options?.title ?? getLabel("dialogs.confirm.title")}
        contentText={options?.content}
        maxWidth="xs"
        additionalActions={
          <Button variant="contained" color={options?.isDestructive === true ? "error" : "primary"} onClick={() => closeDialog(true)}>
            {options?.confirmButtonTitle ?? getLabel("app.buttons.ok")}
          </Button>
        }
      />
    </ConfirmDialogContext.Provider>
  );
}
